// §7.7.3 My Active Edges — self-view for the current medflow_admin user.
// Shows only their own active grants so they can see (and self-revoke)
// which environments they currently have elevated access to.

import { useCallback, useState } from 'react'
import { PageHeader, DataTable, colors, btnDanger } from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import { useAuth } from '../../hooks/use-auth'
import { api } from '../../services/api'

// ── types ─────────────────────────────────────────────────────────────────────

interface StaffGrantRow {
  readonly id: string
  readonly staffUserId: string
  readonly organizationId: string
  readonly environmentId: string | null
  readonly grantedAt: string
  readonly expiresAt: string
  readonly status: 'pending' | 'active' | 'expired' | 'revoked'
}

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(ts: string | null): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ts
  }
}

// ── component ─────────────────────────────────────────────────────────────────

export function MyActiveEdgesPage() {
  const { user } = useAuth()

  const filters: Record<string, string | undefined> = {
    staffUserId: user?.id,
    status: 'active',
  }

  const {
    data: rows,
    total,
    loading,
    error,
    refresh,
  } = useApiList<StaffGrantRow>({
    path: '/admin/staff-grants',
    limit: 100,
    filters,
  })

  const [revoking, setRevoking] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  const handleSelfRevoke = useCallback(
    async (id: string) => {
      setRevoking(id)
      setRevokeError(null)
      try {
        await api.post(`/admin/staff-grants/${id}/revoke`, {})
        refresh()
      } catch (err) {
        setRevokeError(err instanceof Error ? err.message : 'Revoke failed')
      } finally {
        setRevoking(null)
      }
    },
    [refresh],
  )

  const columns: readonly Column<StaffGrantRow>[] = [
    {
      key: 'environmentId',
      header: 'Environment',
      render: (r) =>
        r.environmentId ? (
          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.environmentId}</span>
        ) : (
          <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>org-wide</span>
        ),
    },
    {
      key: 'organizationId',
      header: 'Organization',
      render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.organizationId}</span>,
      sortable: true,
    },
    {
      key: 'grantedAt',
      header: 'Granted At',
      render: (r) => formatDate(r.grantedAt),
      sortable: true,
    },
    {
      key: 'expiresAt',
      header: 'Expires At',
      render: (r) => formatDate(r.expiresAt),
      sortable: true,
    },
    {
      key: 'id',
      header: 'Actions',
      render: (r) => (
        <button
          style={{ ...btnDanger, padding: '4px 10px', fontSize: '0.75rem' }}
          disabled={revoking === r.id}
          onClick={() => handleSelfRevoke(r.id)}
        >
          {revoking === r.id ? 'Revoking...' : 'Self-Revoke'}
        </button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="My Active Edges"
        subtitle={`Active staff-access grants for your account${total > 0 ? ` — ${total.toLocaleString()} active` : ''}`}
      />

      <div style={{ padding: '24px' }}>
        {revokeError && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {revokeError}
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : (
          <DataTable columns={columns} rows={rows} keyField="id" emptyMessage="You have no active edge grants." />
        )}
      </div>
    </div>
  )
}
